import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/material";
import { Form, Formik } from "formik";
import TextInput from "view/pages/MyProfile/Components/Inputs/TextInput";
import { useDispatch } from "react-redux";
import { useDropzone } from "react-dropzone";
import apiLibrary from "services/api";
import { addFormSchema } from "utils/validationSchemas";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Toasts } from "view/components/Toasts";
import Button from "view/components/Button";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { resetDynamicForms } from "store/forms/reducer.actions";
import {
  closeAddFormModalAction,
  openformDetailsAction,
} from "store/modals/reducer.actions";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { fetchDynamicFormsAction } from "store/dynamicForms";
import { fetchFormDetailsAction } from "store/formDetails/reducer.actions";
import { dynamicFormsStatusAction } from "store/filters/reducer.actions";
import { updateColumnsVisibilityByStatus } from "store/directoriesOrders/reducer.actions";
import { MAXIMUM_FILE_SIZE } from "constants/FileSize";
import MultiSelect from "view/components/Multiselect";
import XCloseIcon from "assets/icons/HeroIcons/XCloseIcon";
import CustomCheckbox from "view/components/CheckBox";
import useCustomBranding from "hooks/useCustomBranding";
import { debounce } from "lodash";
import { TailSpin } from "react-loader-spinner";
import CameraIcon from "assets/icons/HeroIcons/CameraIcon";

const style: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "#ffff",
  borderRadius: "10px",
};

/**
 * Component for adding, editing, or using a form as a template.
 * @returns {JSX.Element} The component's JSX representation
 */
export const AddEditTemplateFormModal = () => {
  // State variables

  const { addEditTemplateFormModal } = useSelector(
    (state: RootState) => state.modals
  );
  const [Image, setImage] = useState<any>(null);
  const [allPrograms, setAllPrograms] = useState([]);
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const location = useLocation();
  const locationPathName = location?.pathname?.split("/")[1];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { getBrandPlaceholder } = useCustomBranding();
  const { programId, communityId } = useParams();
  const [formInitialValues, setFormInitialValues] = useState<any>({
    description: "",
    name: "",
    pictureId: null,
    programId: null,
    allowAnonObs: false,
  });
  const [formError, setFormError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Initialize formInitialValues when in editMode or templateMode
  useEffect(() => {
    if (
      addEditTemplateFormModal.editMode ||
      addEditTemplateFormModal.templateMode
    ) {
      const { name, description, pictureId, program } =
        addEditTemplateFormModal?.data;
      setFormInitialValues({
        description,
        name: addEditTemplateFormModal.templateMode ? `Copy of ${name}` : name,
        pictureId: pictureId,
        programId: { value: program?.id, label: program?.name },
        allowAnonObs: addEditTemplateFormModal?.data?.allowAnonObs || false,
      });
    }
  }, [
    addEditTemplateFormModal?.data,
    addEditTemplateFormModal.editMode,
    addEditTemplateFormModal.templateMode,
  ]);

  // Fetch all programs on component mount
  useEffect(() => {
    /**
     * Fetches all programs from the API.
     * @example
     * fetchAllPrograms();
     */

    if (addEditTemplateFormModal.isOpen) {
      fetchAllPrograms();
    }
  }, [addEditTemplateFormModal.isOpen]);

  const fetchAllPrograms = useCallback(
    async (searchQuery?: string, page: number = 1, isLoadMore = false) => {
      setIsLoading(true);
      try {
        const res = await apiLibrary.Forms.getAllProgramsForForms(
          searchQuery,
          page
        );
        const newPrograms = res.data.programs.map((p: any) => ({
          label: p?.name,
          value: p?.id,
        }));
        setAllPrograms((prev) =>
          isLoadMore ? [...prev, ...newPrograms] : newPrograms
        );
        setHasMore(res.data.totalPages > page);
      } catch (error) {
        console.error("Error fetching programs:", error);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Handle form submission.
   * @param {object} values - Form values
   * @param {Function} setSubmitting - Function to set submitting state
   * @example
   * handleSubmit({ name: "Form Name" }, { setSubmitting: (bool) => {} });
   */
  const handleSubmit = async (
    values: any,
    {
      setSubmitting,
      setErrors,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      setErrors: (errors: { [field: string]: string }) => void;
    }
  ) => {
    try {
      const formValues = {
        ...values,
        programId: values.programId?.value,
      };

      if (Image) {
        // Upload the image if available
        const uploadResponse = await apiLibrary.file.fileUpload(Image);
        formValues.pictureId = uploadResponse.data.id;
      }

      if (
        addEditTemplateFormModal.editMode &&
        addEditTemplateFormModal.data.id
      ) {
        // Edit an existing form
        const { data, message } = await apiLibrary.Forms.editForm(
          addEditTemplateFormModal.data.id,
          formValues
        );
        if (addEditTemplateFormModal.data.path === "directory") {
          dispatch(fetchDynamicFormsAction(communityId, programId));
          // dispatch(resetDynamicForms());
          // dispatch(dynamicFormsStatusAction("published"));
        } else {
          dispatch(fetchDynamicFormsAction(communityId, programId));
          dispatch(fetchFormDetailsAction(addEditTemplateFormModal.data));
        }
        Toasts.success(message);
        handleClose();
      } else if (
        addEditTemplateFormModal.templateMode &&
        addEditTemplateFormModal.data.id
      ) {
        // Use form as a template
        const { data, message } = await apiLibrary.Forms.useFormAsTemplate(
          addEditTemplateFormModal.data.id,
          formValues
        );
        navigate(`/forms/${data.id}/edit`, {
          state: {programId: programId,
            locationPathName,
          },
        });

        Toasts.success(message);
        handleClose();
      } else {
        // Add a new form
        const { data, message } = await apiLibrary.Forms.addNewForm(formValues);
        Toasts.success(message);
        handleClose();
        navigate(`/forms/${data.id}/edit`,{
          state: { programId: programId,
            locationPathName,
          },
        });
        // dispatch(dynamicFormsStatusAction("draft"));
        // dispatch(updateColumnsVisibilityByStatus("draft"))
      }
    } catch (error: any) {
      // Handle API errors
      const errorMsg = error?.response?.data?.message ?? error.message;
      if (
        errorMsg ===
        "Looks like this form name is already taken. please enter a different one."
      ) {
        setErrors({
          name: "*Looks like this form name is already taken. please enter a different one.",
        });
      } else {
        Toasts.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle click event to trigger file input click.
   * @example
   * handleClick();
   */
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file drop
  const onDrop = useCallback((acceptedFiles: any) => {
    // Handle dropped files, like uploading or processing
    const fileSize = acceptedFiles[0].size / 1024 / 1024; // convert bytes to mb
    if (fileSize > MAXIMUM_FILE_SIZE) {
      Toasts.error("File size must be less than 10 mb!");
      setImage(null);
      acceptedFiles.splice(0, acceptedFiles.length); // Clear the accepted files

      return;
    }
    setImage(acceptedFiles[0]);
  }, []);

  // Dropzone configuration
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpeg", ".png", ".jpg"],
    },
    maxFiles: 1, // Limit to a single file
  });

  const inputValueRef = useRef("");
  const handleAddProgramSearch = (query: string) => {
    inputValueRef.current = query;
    fetchAllPrograms(query);
  };

  const handleScrollToEnd = async () => {
    if (hasMore && !isLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      setIsLoading(true);
      try {
        await fetchAllPrograms(inputValueRef.current, nextPage, true);
      } catch (err) {
      } finally {
        setIsLoading(false);
      }
    }
  };

  /**
   * Handle modal closure.
   * @example
   * handleClose();
   */
  const handleClose = () => {
    setImage(null);
    dispatch(closeAddFormModalAction());
    setFormInitialValues({
      description: "",
      name: "",
      pictureId: null,
      programId: null,
      allowAnonObs: false,
    });
  };

  const validateFormName = useCallback(
    debounce(
      async (
        programId: string,
        name: string,
        setFieldError: Function,
        setFieldTouched: Function,
        id
      ) => {
        try {
          await apiLibrary.Forms.formNameValidation({
            name,
            program_id: programId,
            ...(addEditTemplateFormModal.editMode && { formId: id }),
          });
          setFormError("");
        } catch (error: any) {
          if (
            error?.response?.data?.message ===
            "Looks like this form name is already taken. Please enter a different one."
          ) {
            setFieldError(
              "name",
              "*Looks like this form name is already taken. Please enter a different one."
            );
            setFormError(
              "*Looks like this form name is already taken. Please enter a different one."
            );
            setFieldTouched("name", true, false);
          }
        }
      },
      100
    ),
    []
  );

  useEffect(() => {
    return () => {
      validateFormName.cancel();
    };
  }, [validateFormName]);

  return (
    <Modal
      open={addEditTemplateFormModal.isOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      disableAutoFocus={true}
      aria-describedby="modal-modal-description"
      className="border-none"
    >
      <Box sx={style} className="dark:bg-secondaryLight ">
        <Formik
          initialValues={formInitialValues}
          validationSchema={addFormSchema}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
            setFieldValue,
            isSubmitting,
            setErrors,
            isValid,
            setFieldError,
            setFieldTouched,
          }) => {
            useEffect(() => {
              if (values.programId?.value && values.name?.trim()) {
                validateFormName(
                  values.programId.value,
                  values.name.trim(),
                  setFieldError,
                  setFieldTouched,
                  addEditTemplateFormModal.editMode &&
                    addEditTemplateFormModal?.data.id
                );
              }
            }, [values.programId?.value, values.name]);
            return (
              <Form>
                <div className="flex flex-col justify-start items-start max-w-[700px] rounded-lg">
                  <div
                    className="relative flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 gap-4 p-6 rounded-lg dark:bg-secondaryLight bg-bgWhite"
                    style={{
                      boxShadow:
                        "0px 2px 8px 0 rgba(2,13,36,0.14), 0px 2px 24px 0 rgba(2,13,36,0.08)",
                    }}
                  >
                    <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 py-0.5">
                      <div className="relative flex items-center self-stretch justify-start flex-grow-0 flex-shrink-0">
                        <p className="flex-grow w-[608px] text-xl font-semibold text-left text-textMid dark:text-textMain">
                          {addEditTemplateFormModal?.data?.id
                            ? addEditTemplateFormModal.editMode
                              ? "Edit Form"
                              : addEditTemplateFormModal.templateMode
                                ? "Use as Template"
                                : "Add Form"
                            : "Add Form"}
                        </p>
                        <button
                          title="close"
                          onClick={handleClose}
                          type="button"
                        >
                          <XCloseIcon
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                          />
                        </button>
                      </div>
                    </div>

                    <div className="flex w-full pr-1 overflow-y-auto items-start justify-start">
                      <div className="flex gap-2 w-[35%] px-3 mt-5">
                        <div className="flex flex-col">
                          <div className="relative flex flex-col items-start self-stretch justify-start flex-grow p-1 rounded-lg">
                            <img
                              src={
                                Image
                                  ? URL.createObjectURL(Image)
                                  : addEditTemplateFormModal?.data?.imageUrl ||
                                    getBrandPlaceholder("moduleCover")
                              }
                              alt="Dropped file"
                              className="relative flex-grow-0 flex-shrink-0 object-cover w-[169px] h-[169px] rounded-lg"
                            />
                            <div
                              // disabled={isLoading.profileImage}
                              {...getRootProps()}
                              onClick={handleClick}
                              className="flex justify-center cursor-pointer items-center absolute bottom-[11px] right-3 gap-2 p-0.5 w-9 h-9 rounded-3xl bg-bgWhite dark:bg-secondaryLight"
                            >
                              <CameraIcon className="w-6 h-6" />
                            </div>
                            <input {...getInputProps()} ref={fileInputRef} />
                          </div>

                          <div className="pt-4">
                            <p className="self-stretch flex-grow-0 flex-shrink-0 w-full text-xs text-left text-textMidLight dark:text-textMain">
                              For optimal display across all devices, use an
                              image sized{" "}
                              <span className="font-semibold">1200x800 px</span>{" "}
                              with a maximum file size of{" "}
                              <span className="font-semibold">10MB</span> . You
                              can crop, resize or edit your image at{" "}
                              <a
                                className="font-medium text-primary"
                                href="https://www.iloveimg.com"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                iloveimg.com
                              </a>
                              .
                            </p>
                          </div>
                        </div>
                        <div className="border-r border-borderLightGray mt-4 h-[300px]"></div>
                      </div>
                      <div className="w-[65%] pl-3 mt-[-17px]">
                        <div className="flex flex-col">
                          <div className="w-full">
                            <AddProgramName
                              handleAddProgramNameOnChange={(option: any) =>
                                setFieldValue("programId", option)
                              }
                              errors={errors}
                              touched={touched}
                              programs={allPrograms}
                              values={values}
                              handleAddProgramSearch={handleAddProgramSearch}
                              isEditMode={addEditTemplateFormModal.editMode}
                              isLoading={isLoading}
                              handleScrollToEnd={handleScrollToEnd}
                            />
                          </div>
                          <TextInput
                            label="Form Name*"
                            type="text"
                            name="name"
                            setFieldValue={setFieldValue}
                            value={values.name}
                            error={errors?.name || formError}
                            touched={
                              errors?.name ===
                              "*Looks like this form name is already taken. Please enter a different one."
                                ? true
                                : touched.name
                            }
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            placeholder="Form Name"
                            disabled={!values.programId?.value}
                          />
                        </div>
                        <div className="mt-[-5px]">
                          <TextInput
                            label="Description*"
                            type="text"
                            name="description"
                            fieldAs="textarea"
                            setFieldValue={setFieldValue}
                            value={values.description}
                            error={errors?.description}
                            touched={touched.description}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            placeholder="Description"
                            helperText={` 6000 Max Characters`}
                          />
                        </div>
                        <div>
                          <CustomCheckbox
                            name="allowAnonObs"
                            checked={values.allowAnonObs}
                            onChange={handleChange}
                            // indeterminate={isFormSectionAllChecked? false: isFormSectionSomeChecked}
                            label={
                              <p className="text-secondaryMid font-Overpass dark:text-textMain">
                                Generate link to allow anonymous observations.
                              </p>
                            }
                            labelPlacement="end"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center self-stretch justify-end flex-grow-0 flex-shrink-0 gap-2">
                      <Button
                        type="button"
                        text="Cancel"
                        filledColor="primary"
                        outlinedColor="primary"
                        textColor="textWhite"
                        className="px-5 py-2 w-24"
                        width="35"
                        height="13"
                        fontStyle="font-semibold"
                        variant="outlined"
                        onClick={handleClose}
                      />

                      <Button
                        disabled={isSubmitting || !isValid || !!formError}
                        type="submit"
                        text={
                          // addEditTemplateFormModal.editMode ? "Submit" : "Next"
                          "Save"
                        }
                        filledColor="primary"
                        outlinedColor="primary"
                        textColor="textWhite"
                        className="px-5 py-2 w-24"
                        width="35"
                        height="13"
                        fontStyle="font-semibold"
                        variant="filled"
                      />
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Modal>
  );
};

const AddProgramName = ({
  touched,
  errors,
  handleAddProgramNameOnChange,
  programs,
  values,
  handleAddProgramSearch,
  isEditMode,
  isLoading,
  handleScrollToEnd,
}: any) => {
  const [inputValue, setInputValue] = useState("");
  const { programId } = values;
  return (
    <div className="flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 gap-2 mt-2">
      <div className="flex items-center self-stretch justify-start flex-grow-0 flex-shrink-0">
        <div className="flex flex-col items-start self-stretch justify-start flex-grow gap-1 rounded">
          <div className="relative flex items-center self-stretch justify-start flex-grow-0 flex-shrink-0 pt-1">
            <p
              className={`flex-grow text-sm font-medium text-left capitalize pb-1 dark:text-caption ${
                !isEditMode && touched.programId && errors.programId
                  ? ""
                  : "text-secondaryMid"
              }`}
            >
              Program Name*
            </p>
          </div>
          <div
            className={`flex justify-start items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-1.5 px-3  rounded bg-white border ${
              !isEditMode && touched.programId && errors.programId
                ? "border-accent_1Dark"
                : "border-lineDark dark:border-lineLight"
            }`}
          >
            <svg
              width={24}
              height={24}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="relative flex-grow-0 flex-shrink-0 w-6 h-6"
              preserveAspectRatio="xMidYMid meet"
            >
              <path
                d="M21.7495 20.6895L16.0855 15.0255C17.4466 13.3914 18.1253 11.2956 17.9805 9.17389C17.8356 7.05219 16.8784 5.06801 15.3079 3.6341C13.7374 2.2002 11.6745 1.42697 9.54844 1.47528C7.42236 1.52359 5.39674 2.38971 3.89298 3.89347C2.38922 5.39723 1.5231 7.42284 1.47479 9.54893C1.42648 11.675 2.19971 13.7379 3.63361 15.3084C5.06752 16.8789 7.0517 17.8361 9.1734 17.981C11.2951 18.1258 13.391 17.4471 15.025 16.086L20.689 21.75L21.7495 20.6895ZM2.99948 9.74996C2.99948 8.41494 3.39536 7.1099 4.13706 5.99987C4.87876 4.88983 5.93296 4.02467 7.16636 3.51378C8.39976 3.00289 9.75696 2.86921 11.0663 3.12966C12.3757 3.39011 13.5784 4.03299 14.5224 4.97699C15.4665 5.921 16.1093 7.12373 16.3698 8.4331C16.6302 9.74248 16.4966 11.0997 15.9857 12.3331C15.4748 13.5665 14.6096 14.6207 13.4996 15.3624C12.3895 16.1041 11.0845 16.5 9.74948 16.5C7.95987 16.498 6.24414 15.7862 4.9787 14.5207C3.71326 13.2553 3.00146 11.5396 2.99948 9.74996Z"
                fill="#9B9FA5"
              />
            </svg>
            <div className="flex flex-col items-start justify-start flex-grow w-full ">
              <MultiSelect
                inputValue={inputValue}
                onInputChange={(value: string) => {
                  setInputValue(value);
                  handleAddProgramSearch(value);
                }}
                placeholder="Program Name"
                options={programs}
                defaultValue={{ value: "", label: "" }}
                isMulti={false}
                value={[programId]}
                isDisable={isEditMode}
                transformY="translateY(0%)"
                onChange={(option: any) => {
                  handleAddProgramNameOnChange(option);
                }}
                isLoading={isLoading}
                onMenuScrollToBottom={handleScrollToEnd}
              />
            </div>
          </div>
          <div className="relative flex items-center self-stretch justify-start flex-grow-0 flex-shrink-0">
            {errors.programId && (
              <p className="flex-grow w-[1/2] text-xs text-left text-accent_1Dark capitalize">
                {errors?.programId}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};